<template>
	<div class="message-content">
		<div
			v-for="(block, i) in parsedBlocks"
			:key="i"
		>
			<div
				v-if="block.type === 'chart'"
				class="my-3"
			>
				<highcharts :options="block.chartOptions" />
			</div>
			<AsyncMap
				v-else-if="block.type === 'map'"
				:url="block.url"
			/>
			<MapView
				v-else-if="block.type === 'geojson'"
				:geojson="block.geojson"
			/>
			<div
				v-else-if="block.type === 'table'"
				style="margin: 12px 0; overflow-x: auto"
			>
				<table
					style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccd7df"
				>
					<thead>
						<tr>
							<th
								v-for="(h, hi) in block.headers"
								:key="hi"
								style="
									padding: 6px 10px;
									text-align: left;
									background: #f1f5f8;
									color: #01395e;
									font-weight: 600;
									border: 1px solid #ccd7df;
								"
							>
								{{ h }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="(row, ri) in block.rows"
							:key="ri"
							:style="{ background: ri % 2 === 0 ? '#ffffff' : '#f9fafb' }"
						>
							<td
								v-for="(cell, ci) in row"
								:key="ci"
								style="padding: 6px 10px; border: 1px solid #ccd7df"
							>
								{{ cell }}
							</td>
						</tr>
					</tbody>
				</table>
				<div style="display: flex; gap: 8px; margin-top: 8px">
					<button
						style="
							display: inline-flex;
							align-items: center;
							gap: 4px;
							font-size: 12px;
							padding: 4px 10px;
							border-radius: 4px;
							border: 1px solid #d1e7dd;
							background: #ecfdf5;
							color: #166534;
							cursor: pointer;
						"
						@click="downloadCsv(block)"
					>
						📥 CSV
					</button>
					<button
						style="
							display: inline-flex;
							align-items: center;
							gap: 4px;
							font-size: 12px;
							padding: 4px 10px;
							border-radius: 4px;
							border: 1px solid #e5e7eb;
							background: #f9fafb;
							color: #374151;
							cursor: pointer;
						"
						@click="downloadMd(block)"
					>
						📥 Markdown
					</button>
				</div>
			</div>
			<div
				v-else
				v-html="block.html"
				class="prose prose-sm max-w-none"
			></div>
		</div>
	</div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import AsyncMap from './AsyncMap.vue'
import MapView from './MapView.vue'

export default {
	name: 'MessageContent',
	components: { highcharts: Chart, AsyncMap, MapView },
	props: {
		content: { type: String, default: '' },
	},
	computed: {
		tenantId() {
			try {
				const jwt = localStorage.getItem('jwt')
				if (!jwt) return 'default'
				const token = jwt.replace('Bearer ', '')
				const payload = JSON.parse(atob(token.split('.')[1]))
				return payload.customer_id || 'default'
			} catch {
				return 'default'
			}
		},
		parsedBlocks() {
			const blocks = []
			const text = this.content || ''
			// Split by code blocks
			const parts = text.split(/(```[\s\S]*?```)/g)
			for (const part of parts) {
				const chartMatch = part.match(/```chart\s*\n([\s\S]*?)\n```/)
				if (chartMatch) {
					try {
						const data = JSON.parse(chartMatch[1])
						blocks.push({ type: 'chart', chartOptions: this.toHighcharts(data) })
					} catch {
						blocks.push({ type: 'html', html: this.renderMarkdown(part) })
					}
					continue
				}
				// Check for MAP markers [MAP:fazenda:14] or [MAP:talhao:14:vtcc]
				const mapMatch = part.match(/\[MAP:(fazenda|talhao):([^\]]+)\]/)
				if (mapMatch) {
					const tipo = mapMatch[1]
					const params = mapMatch[2].trim()
					const tenant = this.tenantId || 'default'
					const url = '/api/geo/' + tipo + '/' + tenant + '/' + params.replace(':', '/')
					blocks.push({ type: 'map', url })
					continue
				}
				// Check for markdown tables
				const tableData = this.extractTable(part)
				if (tableData) {
					blocks.push({ type: 'table', ...tableData, raw: part })
					continue
				}
				if (part.trim()) {
					blocks.push({ type: 'html', html: this.renderMarkdown(part) })
				}
			}
			return blocks.length ? blocks : [{ type: 'html', html: this.renderMarkdown(text) }]
		},
	},
	methods: {
		toHighcharts(data) {
			return {
				chart: { type: data.type || 'line' },
				title: { text: data.title || '' },
				xAxis: { categories: data.labels || [], title: { text: data.xLabel || '' } },
				yAxis: { title: { text: data.yLabel || '' } },
				series: (data.datasets || []).map((d) => ({ name: d.label, data: d.data })),
				credits: { enabled: false },
			}
		},
		extractTable(text) {
			const lines = text.trim().split('\n')
			const tableLines = lines.filter((l) => l.includes('|'))
			if (tableLines.length < 3) return null
			const sep = tableLines.findIndex((l) => /^\|?\s*[-:]+/.test(l))
			if (sep < 1) return null
			const parse = (l) =>
				l
					.split('|')
					.map((c) => c.trim())
					.filter(Boolean)
			const headers = parse(tableLines[sep - 1])
			const rows = tableLines.slice(sep + 1).map(parse)
			return { headers, rows }
		},
		escapeHtml(text) {
			return text
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;')
		},
		safeUrl(url) {
			// Navegadores ignoram espaços e controles ao resolver o esquema, então testa sem eles
			const probe = url.replace(/[\s\u0000-\u001f]/g, '').toLowerCase()
			const scheme = probe.match(/^([a-z][a-z0-9+.-]*):/)
			if (scheme) return ['http', 'https', 'mailto'].includes(scheme[1]) ? url : null
			return url
		},
		renderMarkdown(text) {
			// O escape vem primeiro: depois dele as únicas tags do resultado são as inseridas
			// abaixo, então nada vindo do LLM pode virar HTML. Nenhum metacaractere de markdown
			// é afetado pelo escape, por isso os padrões seguintes continuam casando.
			let html = this.escapeHtml(text)
				.replace(
					/```(\w*)\n([\s\S]*?)```/g,
					'<pre class="bg-gray-100 p-3 rounded text-xs overflow-x-auto"><code>$2</code></pre>',
				)
				.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
				.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
				.replace(/\*(.+?)\*/g, '<em>$1</em>')
				.replace(/^### (.+)$/gm, '<h3 class="text-base font-bold mt-2 mb-1">$1</h3>')
				.replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-2 mb-1">$1</h2>')
				.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-2 mb-1">$1</h1>')
				.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, url) => {
					const href = this.safeUrl(url)
					if (!href) return label
					return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-[#95BB49] underline">${label}</a>`
				})
				.replace(/\n/g, '<br>')
			return html
		},
		downloadCsv(block) {
			const csv = [block.headers.join(','), ...block.rows.map((r) => r.join(','))].join('\n')
			this.download(csv, 'table.csv', 'text/csv')
		},
		downloadMd(block) {
			const md = [
				'| ' + block.headers.join(' | ') + ' |',
				'| ' + block.headers.map(() => '---').join(' | ') + ' |',
				...block.rows.map((r) => '| ' + r.join(' | ') + ' |'),
			].join('\n')
			this.download(md, 'table.md', 'text/markdown')
		},
		download(content, filename, type) {
			const blob = new Blob([content], { type })
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = filename
			a.click()
			URL.revokeObjectURL(url)
		},
	},
}
</script>

<style scoped>
.message-content {
	font-size: 0.875rem;
	line-height: 1.6;
	color: #272728;
}
.message-content >>> h1,
.message-content >>> h2,
.message-content >>> h3 {
	color: #272728;
	margin: 0.5em 0 0.3em;
	font-weight: 600;
}
.message-content >>> p {
	margin: 0.4em 0;
}
.message-content >>> code {
	background: #f1f1f1;
	border-radius: 4px;
	padding: 2px 6px;
	font-size: 0.85em;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.message-content >>> pre {
	background: #1e1e1e;
	color: #d4d4d4;
	border-radius: 8px;
	padding: 12px;
	overflow-x: auto;
	margin: 0.5rem 0;
}
.message-content >>> pre code {
	background: transparent;
	color: inherit;
	padding: 0;
}
.message-content >>> table {
	border-collapse: collapse;
	width: 100%;
	margin: 0.5em 0;
}
.message-content >>> th,
.message-content >>> td {
	border: 1px solid #ccd7df;
	text-align: left;
	padding: 6px 10px;
}
.message-content >>> th {
	background: #f1f5f8;
	color: #01395e;
	font-weight: 600;
}
.message-content >>> ul,
.message-content >>> ol {
	margin: 0.4em 0;
	padding-left: 1.5em;
}
.message-content >>> li {
	margin: 0.2em 0;
}
.message-content >>> blockquote {
	border-left: 3px solid #95bb49;
	color: #737576;
	margin: 0.5em 0;
	padding-left: 12px;
}
.message-content >>> a {
	color: #34617e;
	text-decoration: underline;
}
.message-content >>> a:hover {
	color: #01395e;
}
.message-content >>> strong {
	font-weight: 600;
}
</style>
